import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import { useOperadoras } from '../dominio/operadoras.ts';
import { CampoCaixa, CampoSelecao, CampoTexto } from './Campo.tsx';
import { Botao, Pilula } from './Superficie.tsx';

export type ModeloDeConferencia = {
  id: string;
  produto: string;
  operadora: string | null;
  ativo: boolean;
  itens: string[];
};

/** O menor item que o `PUT` aceita, espelhado do esquema da rota. */
const MINIMO_DO_ITEM = 3;
const MAXIMO_DE_ITENS = 40;

/** "Odonto · todas as operadoras", o mesmo rótulo da linha fechada. */
function nomeDoRoteiro(modelo: { produto: string; operadora: string | null }): string {
  return `${modelo.produto}${modelo.operadora ? ` · ${modelo.operadora}` : ' · todas as operadoras'}`;
}

type CorpoDoSalvamento = {
  produto: string;
  operadora?: string;
  ativo: boolean;
  itens: string[];
};

/**
 * Roteiros de conferência da operação.
 *
 * Cada roteiro abre um por vez. Sete campos de texto por roteiro, três
 * roteiros abertos, e o cartão vira uma parede onde não se acha o passo que se
 * veio corrigir — fechado, a linha continua sendo a do protótipo.
 */
export function RoteirosDeConferencia({
  modelos,
}: {
  modelos: ModeloDeConferencia[];
}): React.JSX.Element {
  const [aberto, definirAberto] = useState<string | null>(null);

  return (
    <div>
      <ul>
        {modelos.map((modelo) => (
          <li key={modelo.id} data-teste="roteiro-conferencia" className="border-b border-linha last:border-0">
            <button
              type="button"
              data-teste="abrir-roteiro"
              onClick={() => definirAberto((atual) => (atual === modelo.id ? null : modelo.id))}
              aria-expanded={aberto === modelo.id}
              className="flex w-full items-center justify-between gap-3 py-3 text-left transition hover:bg-marinho-suave"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium text-marinho">
                  {nomeDoRoteiro(modelo)}
                </span>
                <span className="block truncate text-xs text-texto-suave">
                  {modelo.itens.length} {modelo.itens.length === 1 ? 'item' : 'itens'}
                </span>
              </span>
              <span className="shrink-0">
                <Pilula tom={modelo.ativo ? 'sucesso' : 'neutro'}>
                  {modelo.ativo ? 'Ativo' : 'Inativo'}
                </Pilula>
              </span>
            </button>

            {/* Monta só depois do clique, e some ao fechar: o rascunho não
                salvo morre junto, em vez de reaparecer horas depois parecendo
                o que está valendo. */}
            {aberto === modelo.id ? <CorpoDoRoteiro modelo={modelo} /> : null}
          </li>
        ))}
      </ul>

      {modelos.length === 0 ? <p className="text-texto-suave">Nenhum roteiro cadastrado.</p> : null}

      <NovoRoteiro />
    </div>
  );
}

/**
 * Leitura e edição de um roteiro que já existe.
 *
 * Produto e operadora aparecem no título e não em campo: o par é a identidade
 * do roteiro na rota, que cria quando não encontra e atualiza quando encontra.
 * Editar o texto ali não renomearia nada — deixaria o roteiro antigo de pé e
 * abriria um segundo ao lado. Para tirar um de circulação, existe "Ativo".
 */
function CorpoDoRoteiro({ modelo }: { modelo: ModeloDeConferencia }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [itens, definirItens] = useState<string[]>(modelo.itens);
  const [ativo, definirAtivo] = useState(modelo.ativo);
  const [salvo, definirSalvo] = useState(false);

  const salvar = useMutation({
    mutationFn: (corpo: CorpoDoSalvamento) =>
      chamarApi('/configuracoes/checklists', { metodo: 'PUT', corpo }),
    onSuccess: async () => {
      definirSalvo(true);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['configuracoes'] });
    },
  });

  function alterar(proximos: string[]): void {
    definirSalvo(false);
    definirItens(proximos);
  }

  function mover(indice: number, destino: number): void {
    if (destino < 0 || destino >= itens.length) return;
    const proximos = [...itens];
    const [passo] = proximos.splice(indice, 1);
    proximos.splice(destino, 0, passo);
    alterar(proximos);
  }

  const curtos = itens.some((item) => item.trim().length < MINIMO_DO_ITEM);

  return (
    <div className="pb-4">
      {salvo ? (
        <p
          data-teste="roteiro-conferencia-salvo"
          className="mb-3 rounded-lg bg-sucesso/10 px-3 py-2 text-sm text-sucesso"
        >
          Roteiro salvo. As implantações em andamento seguem com o roteiro que já copiaram.
        </p>
      ) : null}

      <ol className="space-y-2">
        {itens.map((item, indice) => (
          <li
            key={indice}
            data-teste="item-de-roteiro"
            className="flex flex-wrap items-end gap-2 rounded-lg bg-marinho-suave p-2"
          >
            <CampoTexto
              rotulo={`Passo ${indice + 1}`}
              className="min-w-[12rem] flex-1"
              value={item}
              maxLength={200}
              onChange={(evento) =>
                alterar(itens.map((atual, posicao) => (posicao === indice ? evento.target.value : atual)))
              }
            />
            <div className="flex gap-2" role="group" aria-label={`Passo ${indice + 1}`}>
              <Botao onClick={() => mover(indice, indice - 1)} desabilitado={indice === 0}>
                Subir
              </Botao>
              <Botao
                onClick={() => mover(indice, indice + 1)}
                desabilitado={indice === itens.length - 1}
              >
                Descer
              </Botao>
              <Botao
                onClick={() => alterar(itens.filter((_, posicao) => posicao !== indice))}
                desabilitado={itens.length === 1}
              >
                Remover
              </Botao>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Botao
          onClick={() => alterar([...itens, ''])}
          desabilitado={itens.length >= MAXIMO_DE_ITENS}
        >
          Adicionar passo
        </Botao>
        <CampoCaixa
          rotulo="Ativo"
          className="px-1"
          checked={ativo}
          onChange={(evento) => {
            definirSalvo(false);
            definirAtivo(evento.target.checked);
          }}
        />
        <Botao
          variante="primario"
          teste="salvar-roteiro-conferencia"
          desabilitado={salvar.isPending || curtos}
          onClick={() =>
            salvar.mutate({
              produto: modelo.produto,
              operadora: modelo.operadora ?? undefined,
              ativo,
              itens: itens.map((item) => item.trim()),
            })
          }
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar roteiro'}
        </Botao>
      </div>

      {curtos ? (
        <p className="mt-2 text-xs text-texto-suave">
          Cada passo precisa de ao menos {MINIMO_DO_ITEM} letras. Remova o que sobrou em branco.
        </p>
      ) : null}

      {salvar.isError ? (
        <p role="alert" className="mt-2 text-sm text-erro">
          {salvar.error instanceof ErroDaApi
            ? salvar.error.message
            : 'Não foi possível salvar o roteiro.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Criação de um roteiro para um par produto + operadora que ainda não tem um.
 *
 * Pede só o primeiro passo. O resto entra pela edição do roteiro recém-criado,
 * que é a mesma tela — pedir os sete aqui seria um formulário que ninguém
 * termina de primeira.
 */
function NovoRoteiro(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [aberto, definirAberto] = useState(false);
  const [produto, definirProduto] = useState('');
  const [operadora, definirOperadora] = useState('');
  const [primeiroPasso, definirPrimeiroPasso] = useState('');
  const operadoras = useOperadoras();

  const criar = useMutation({
    mutationFn: (corpo: CorpoDoSalvamento) =>
      chamarApi('/configuracoes/checklists', { metodo: 'PUT', corpo }),
    onSuccess: async () => {
      definirProduto('');
      definirOperadora('');
      definirPrimeiroPasso('');
      definirAberto(false);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['configuracoes'] });
    },
  });

  if (!aberto) {
    return (
      <div className="mt-4 border-t border-linha pt-4">
        <Botao teste="abrir-novo-roteiro" onClick={() => definirAberto(true)}>
          + Roteiro de conferência
        </Botao>
      </div>
    );
  }

  return (
    <form
      data-teste="novo-roteiro-conferencia"
      className="mt-4 border-t border-linha pt-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        criar.mutate({
          produto: produto.trim(),
          operadora: operadora.trim() || undefined,
          ativo: true,
          itens: [primeiroPasso.trim()],
        });
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <CampoTexto
          rotulo="Produto"
          value={produto}
          maxLength={120}
          onChange={(evento) => definirProduto(evento.target.value)}
        />
        <CampoSelecao
          rotulo="Operadora"
          value={operadora}
          onChange={(evento) => definirOperadora(evento.target.value)}
          dica="Todas: o roteiro vale para qualquer operadora do produto."
        >
          <option value="">Todas</option>
          {operadoras.nomes.map((nome) => (
            <option key={nome} value={nome}>
              {nome}
            </option>
          ))}
        </CampoSelecao>
      </div>

      <CampoTexto
        rotulo="Primeiro passo"
        className="mt-2"
        value={primeiroPasso}
        maxLength={200}
        onChange={(evento) => definirPrimeiroPasso(evento.target.value)}
        dica="Os demais passos entram depois, abrindo o roteiro criado."
      />

      <div className="mt-3 flex gap-2">
        <Botao
          tipo="submit"
          variante="primario"
          desabilitado={
            criar.isPending ||
            produto.trim().length < 2 ||
            primeiroPasso.trim().length < MINIMO_DO_ITEM
          }
        >
          {criar.isPending ? 'Criando…' : 'Criar roteiro'}
        </Botao>
        <Botao onClick={() => definirAberto(false)}>Cancelar</Botao>
      </div>

      {criar.isError ? (
        <p role="alert" className="mt-2 text-sm text-erro">
          {criar.error instanceof ErroDaApi
            ? criar.error.message
            : 'Não foi possível criar o roteiro.'}
        </p>
      ) : null}
    </form>
  );
}
